import { Capacitor } from "@capacitor/core"
import { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { authClient } from "~/lib/auth-client"
import { api } from "~/trpc/react"

export function NativeNotifications() {
	const navigate = useNavigate()
	const { data: session } = authClient.useSession()
	const registerDevice = api.notifications.registerNativeDevice.useMutation()
	const userId = session?.user.id

	useEffect(() => {
		if (!userId || !Capacitor.isNativePlatform()) return

		let disposed = false
		const handles: { remove: () => Promise<void> }[] = []

		const setup = async () => {
			try {
				const { PushNotifications } =
					await import("@capacitor/push-notifications")

				handles.push(
					await PushNotifications.addListener("registration", (token) => {
						registerDevice.mutate({ platform: "ios", token: token.value })
					})
				)
				handles.push(
					await PushNotifications.addListener("registrationError", (error) => {
						console.error("Native notification registration failed", error)
					})
				)
				handles.push(
					await PushNotifications.addListener(
						"pushNotificationReceived",
						(notification) => {
							const actionUrl = notification.data?.actionUrl
							toast(notification.title ?? "Orbit", {
								description: notification.body,
								action:
									typeof actionUrl === "string" && actionUrl.startsWith("/")
										? {
												label: "Open",
												onClick: () => navigate(actionUrl)
											}
										: undefined
							})
						}
					)
				)
				handles.push(
					await PushNotifications.addListener(
						"pushNotificationActionPerformed",
						(action) => {
							const actionUrl = action.notification.data?.actionUrl
							if (typeof actionUrl === "string" && actionUrl.startsWith("/")) {
								navigate(actionUrl)
							}
						}
					)
				)

				let permission = await PushNotifications.checkPermissions()
				if (permission.receive === "prompt") {
					permission = await PushNotifications.requestPermissions()
				}
				if (!disposed && permission.receive === "granted") {
					await PushNotifications.register()
				}
			} catch (error) {
				console.error("Native notification setup failed", error)
			}
		}

		setup()

		return () => {
			disposed = true
			void Promise.all(handles.map((handle) => handle.remove()))
		}
	}, [navigate, registerDevice.mutate, userId])

	return null
}
